/**
 * TurnstileWidget — Cloudflare Turnstile captcha wrapper.
 *
 * Renders nothing when no sitekey is configured (captcha disabled — e.g. the
 * local environment), matching the backend, which only enforces tokens when
 * its secret is set. Tokens are single-use: parents must call reset() (via
 * ref) after every protected request so a fresh token is produced.
 *
 * Usage:
 *   const captchaRef = useRef<TurnstileHandle>(null);
 *   const [token, setToken] = useState<string | null>(null);
 *   <TurnstileWidget ref={captchaRef} onToken={setToken} />
 *   // submit with token, then: captchaRef.current?.reset(); setToken(null);
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { getTurnstileSiteKey } from '../config/environment';
import styles from './turnstile-widget.module.css';

/** Minimal typing of the Turnstile global injected by the Cloudflare script */
interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'flexible' | 'compact';
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Single shared script load across all widget instances
let scriptPromise: Promise<void> | null = null;
function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Turnstile'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export interface TurnstileHandle {
  /** Resets the widget so a fresh (single-use) token is produced */
  reset: () => void;
}

interface TurnstileWidgetProps {
  /** Receives the fresh token, or null when it expires/errors/resets */
  onToken: (token: string | null) => void;
  /**
   * Visual theme. Defaults to 'dark' because the whole portal is dark now —
   * 'auto' follows the OS preference and would render a white captcha box in
   * the middle of a dark card for anyone on a light desktop.
   */
  theme?: 'light' | 'dark' | 'auto';
}

export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ onToken, theme = 'dark' }, ref) {
    const siteKey = getTurnstileSiteKey();
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    // Keep the latest callback without re-rendering the widget. Assigning to a
    // ref during render is what the rule flags; an effect is where React allows
    // it, and the timing is equivalent here because the ref is only read from
    // the Turnstile callback, which cannot fire before paint.
    const onTokenRef = useRef(onToken);
    useEffect(() => {
      onTokenRef.current = onToken;
    }, [onToken]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current !== null) {
          window.turnstile?.reset(widgetIdRef.current);
          onTokenRef.current(null);
        }
      },
    }));

    useEffect(() => {
      if (!siteKey || !containerRef.current) return;
      let cancelled = false;

      void loadTurnstileScript()
        .then(() => {
          if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
          widgetIdRef.current = window.turnstile!.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => onTokenRef.current(token),
            'expired-callback': () => onTokenRef.current(null),
            'error-callback': () => onTokenRef.current(null),
            theme,
            // Stretch to the form width so it blends with the inputs
            size: 'flexible',
          });
        })
        .catch(() => {
          // Script blocked/unreachable: leave the container empty; the backend
          // will reject the request and the form shows its own error
        });

      return () => {
        cancelled = true;
        if (widgetIdRef.current !== null) {
          window.turnstile?.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [siteKey, theme]);

    if (!siteKey) return null;
    return <div ref={containerRef} className={styles.turnstileWidget} />;
  },
);
